import { useMemo } from 'react'
import dayjs from 'dayjs'

const normalizeString = value => String(value || '').trim()

export const useLinkageFilters = (
  dataModal,
  { searchKeyword, porcentajeRange, tempFilters, total, linkageComprobantes, isPartialConciliation, valoresAprox }
) => {
  const linkageIds = useMemo(() => {
    if (!Array.isArray(dataModal?.linkage)) return []
    return dataModal.linkage
      .flatMap(item => {
        const ids = [
          item?.idPrincipal,
          item?.id_principal,
          item?.id,
          item?.comprobante,
          item?.numeroFolio,
          item?.folio,
          item?.referenciaCredito
        ]
        if (item?.deuda) {
          ids.push(item.deuda.id, item.deuda.idPrincipal, item.deuda.id_principal)
        }
        if (item?.egreso) {
          ids.push(item.egreso.id, item.egreso.idPrincipal, item.egreso.id_principal)
        }
        if (item?.ingreso) {
          ids.push(item.ingreso.id, item.ingreso.idPrincipal, item.ingreso.id_principal)
        }
        return ids
      })
      .filter(Boolean)
      .map(token => normalizeString(token))
  }, [dataModal?.linkage])

  const linkageIdsSet = useMemo(() => new Set(linkageIds), [linkageIds])

  const doesRowMatchLinkage = row => {
    if (!row) return false
    const candidates = [
      row?.idPrincipal,
      row?.id_principal,
      row?.id,
      row?.comprobante,
      row?.numeroFolio,
      row?.folio,
      row?.referenciaCredito,
      row?.egreso?.idPrincipal,
      row?.egreso?.id_principal,
      row?.egreso?.id,
      row?.egreso?.comprobante,
      row?.ingreso?.idPrincipal,
      row?.ingreso?.id_principal,
      row?.ingreso?.id,
      row?.ingreso?.comprobante,
      row?.linkage?.idPrincipal,
      row?.linkage?.id_principal
    ]
    return candidates
      .filter(Boolean)
      .some(token => linkageIdsSet.has(normalizeString(token)))
  }

  const filterItems = arr => {
    if (!Array.isArray(arr)) return []
    return arr.filter(item => {
      if (!item) return false
      if ((item?.nombreCuentaBanco || '').trim().length > 0) return false
      const status = item?.status?.toUpperCase()
      const isPagadoOCobrado = [
        'PAGADO',
        'COBRADO',
        'PARCIALMENTE PAGADO',
        'PARCIALMENTE COBRADO'
      ].includes(status)

      if (linkageIdsSet.size === 0) {
        return !isPagadoOCobrado
      }

      const matches = doesRowMatchLinkage(item)
      if (isPagadoOCobrado) {
        return matches
      }
      return true
    })
  }

  const enrichCompensacion = row => {
    const monto = row.montoTotal
    const porcentaje = (() => {
      const diff = Math.abs(monto - total)
      const pct = (1 - diff / total) * 100
      return Math.max(pct, 0)
    })()
    return { ...row, porcentaje }
  }

  const applyCompFilters = (rows = []) => {
    if (!Array.isArray(rows)) return []
    const keyword = searchKeyword.trim().toLowerCase()

    const filteredRows = rows
      .map(enrichCompensacion)
      .filter(item => {
        const statusTextPrimary = (item?.status || '').toUpperCase()
        const razonTmp = item?.razonSocial ?? ''
        const cuentaTmp = item?.nombreCuenta ?? item?.cuentaCorriente ?? ''
        const montoTmp = Number(
          item.montoTotal ??
            item.montoRelacionado ??
            item.montoAbono ??
            item.montoCargo ??
            item.montoEditable ??
            0
        )
        if (!razonTmp && !cuentaTmp && statusTextPrimary === 'CONCILIADO' && montoTmp === 0) return false
        const cuentaBanco = (item?.nombreCuentaBanco || '').trim().toLowerCase()
        if (cuentaBanco.length > 0) return false
        const statusText = statusTextPrimary
        if (statusText === 'CONCILIADO') return false
        const montoRow = Number(
          item.montoTotal ??
            item.montoRelacionado ??
            item.montoAbono ??
            item.montoCargo ??
            item.montoEditable ??
            0
        )
        if (!Number.isFinite(montoRow) || montoRow <= 0) return false
        const razon = item.razonSocial?.toLowerCase() || ''
        const folio = String(item.razonSocial || item.nombreCuenta || '').toLowerCase()
        const matchesKeyword = !keyword || razon.includes(keyword) || folio.includes(keyword)
        const matchesPorcentaje =
          item.porcentaje >= porcentajeRange[0] && item.porcentaje <= porcentajeRange[1]
        const itemDate = dayjs(item.fecha).format('YYYY-MM-DD')
        const matchesDate =
          (!tempFilters.fechaInicio || itemDate >= tempFilters.fechaInicio) &&
          (!tempFilters.fechaFin || itemDate <= tempFilters.fechaFin)

        return matchesKeyword && matchesDate && matchesPorcentaje
      })

    return valoresAprox
      ? [...filteredRows].sort((a, b) => b.porcentaje - a.porcentaje)
      : filteredRows
  }

  const getRowIdentifiers = row => {
    if (!row) return []
    return [
      row.comprobante,
      row.idPrincipal,
      row.id_principal,
      row.id,
      row.numeroFolio,
      row.folio,
      row.referenciaCredito,
      row.egreso?.idPrincipal,
      row.egreso?.id_principal,
      row.egreso?.id,
      row.egreso?.comprobante,
      row.ingreso?.idPrincipal,
      row.ingreso?.id_principal,
      row.ingreso?.id,
      row.ingreso?.comprobante,
      row.linkage?.idPrincipal,
      row.linkage?.id_principal
    ]
      .filter(Boolean)
      .map(v => normalizeString(v))
  }

  const isRowInLinkage = row => doesRowMatchLinkage(row)

  const orderByLinkage = rows => {
    if (!Array.isArray(rows)) return []
    const inLinkage = []
    const notInLinkage = []
    rows.forEach(row => {
      if (isRowInLinkage(row)) inLinkage.push(row)
      else notInLinkage.push(row)
    })
    return [...inLinkage, ...notInLinkage]
  }

  const applyLinkageLogic = rows => {
    if (!Array.isArray(rows)) return []

    if (isPartialConciliation) {
      return rows
    }

    return rows.filter(item => {
      const status = item.status?.toUpperCase()
      const isPending = status === 'PENDIENTE' || status === 'PARCIALMENTE CONCILIADO'
      const isMatch = isRowInLinkage(item)
      return isPending || isMatch
    })
  }

  return {
    linkageIds,
    linkageIdsSet,
    filterItems,
    applyCompFilters,
    applyLinkageLogic,
    orderByLinkage,
    getRowIdentifiers
  }
}
